import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectMaximumTravelDistance } from '../../../store/dataSelectors';
import {
    clearMaximumTravelDistance,
    clearTransportMethod,
    clearWorkConditionalityGroup,
    loadMaximumTravelDistanceDetails,
    loadTransportMethodDetails,
    loadWorkConditionalityGroupDetails
} from '../../../store/directusService';
import {
    loadParticipantWorkConditionality,
    updateParticipantWorkConditionality
} from '../../../store/participantService';
import { getObjFromId } from '../../../utils/directusFunctions';
import { NO_CURRENT_PARTICIPANT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import {
    MAXIMUM_TRAVEL_DISTANCE_DETAILS_ERROR,
    TRANSPORT_METHOD_DETAILS_ERROR,
    WORK_CONDITIONALITY_GROUPS_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { formatFloat } from '../../../utils/numberUtils';
import { clearKeys } from '../../../utils/objectUtils';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import SingleSelect from '../../formElements/SingleSelect';
import TextAreaField from '../../formElements/TextAreaField';
import TextInputField from '../../formElements/TextInputField';
import TimeSelect from '../../formElements/TimeSelect';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { NOTES_CHAR_LIMIT, validate } from './validateWorkRequirements';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import local from './workRequirements.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const editOnClosedRoles = [SUPERUSER, QUALITY];

const _MESSAGE = 'Participant work conditionality details have been updated';

const WorkRequirements = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        groupId: '',
        minimumHoursPerWeek: null,
        maximumHoursPerWeek: null,
        weekdayStartTime: '',
        weekdayEndTime: '',
        saturdayStartTime: '',
        saturdayEndTime: '',
        sundayStartTime: '',
        sundayEndTime: '',
        workingHoursNotes: '',
        maximumTravelToWorkId: '',
        participantId: '',
        methodOfTransportIds: []
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [isDisabled, setIsDisabled] = useState(false);
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [keys, setKeys] = useState({
        groupId: '0',
        maximumTravelToWorkId: '1',
        methodOfTransportIds: '2'
    });

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const maximumTravelDistanceDetails = useSelector(selectMaximumTravelDistance);
    const transportMethodDetails = useSelector(
        (state) => state.entities.directusService.transportMethodDetails
    );
    const workConditionalityGroupDetails = useSelector(
        (state) => state.entities.directusService.workConditionalityGroupDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    let currentParticipantWorkConditionality = useSelector(
        (state) => state.entities.participantService.currentParticipantWorkConditionality
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(
        () => () => {
            cleanUp();
        },
        []
    );

    useEffect(() => {
        if (!('id' in currentParticipant)) return;
        dispatch(loadParticipantWorkConditionality(currentParticipant?.id));
        workConditionalityGroupDetails?.length < 1 &&
            dispatch(loadWorkConditionalityGroupDetails());
        maximumTravelDistanceDetails?.length < 1 && dispatch(loadMaximumTravelDistanceDetails());
        transportMethodDetails?.length < 1 && dispatch(loadTransportMethodDetails());

        const disableOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(editOnClosedRoles, roles);
        setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus);
    }, [currentParticipant, roles]);

    useEffect(() => {
        if (Object.keys(currentParticipantWorkConditionality)?.length) {
            setNewEntry(currentParticipantWorkConditionality);
            setInitialStateOnEntry(currentParticipantWorkConditionality);
        }
    }, [currentParticipantWorkConditionality]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _MESSAGE) setIsButtonDisabled(false);
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (Object.keys(currentParticipant)?.length < 1) return NO_CURRENT_PARTICIPANT_ERROR;
        if (maximumTravelDistanceDetails.length < 1) return MAXIMUM_TRAVEL_DISTANCE_DETAILS_ERROR;
        if (transportMethodDetails.length < 1) return TRANSPORT_METHOD_DETAILS_ERROR;
        if (workConditionalityGroupDetails.length < 1) return WORK_CONDITIONALITY_GROUPS_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    const cleanUp = () => {
        dispatch(clearWorkConditionalityGroup());
        dispatch(clearMaximumTravelDistance());
        dispatch(clearTransportMethod());
        setKeys(clearKeys(keys));
        setNewEntry(initialState);
        setInitialStateOnEntry(initialState);
        setErrors({});
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateTimes = (key, value, secondKey) => {
        clearError(key);
        secondKey && clearError(secondKey);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, initialStateOnEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const payload = {
            ...newEntry,
            participantId: currentParticipant.id
        };
        dispatch(updateParticipantWorkConditionality(payload, _MESSAGE));
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={local.wrapper}>
            <form onSubmit={onSubmit} className={form.form} data-testid="work_requirements_form">
                <SingleSelect
                    id="groupId"
                    key={keys.groupId}
                    label="Work Conditionality Group"
                    placeholder="Select work conditionality group..."
                    disabled={isDisabled}
                    selectedId={newEntry.groupId || ''}
                    selected={getObjFromId(workConditionalityGroupDetails, newEntry.groupId)}
                    menuItems={workConditionalityGroupDetails || []}
                    onChange={(chosenId) => onUpdate('groupId', chosenId)}
                />

                <h2 className={app.sectionHeading}>Work Preferences</h2>
                <div className={form.formSection}>
                    <div className={local.sectionColumn1}>
                        <div className={app.fieldHeading}>Working Hours</div>
                        <TextInputField
                            id={'minimumHoursPerWeek'}
                            label="Minimum Hours Required Per Week"
                            placeholder={'Enter minimum hours...'}
                            disabled={isDisabled}
                            mandatory={newEntry.maximumHoursPerWeek}
                            maxLength={5}
                            value={newEntry.minimumHoursPerWeek || ''}
                            error={errors.minimumHoursPerWeek}
                            onChange={(e) =>
                                onUpdateTimes(
                                    'minimumHoursPerWeek',
                                    formatFloat(e.target.value),
                                    'maximumHoursPerWeek'
                                )
                            }
                        />
                        <TextInputField
                            id={'maximumHoursPerWeek'}
                            label="Maximum Hours Required Per Week"
                            placeholder={'Enter maximum hours...'}
                            disabled={isDisabled}
                            mandatory={newEntry.minimumHoursPerWeek}
                            maxLength={5}
                            value={newEntry.maximumHoursPerWeek || ''}
                            error={errors.maximumHoursPerWeek}
                            onChange={(e) =>
                                onUpdateTimes(
                                    'maximumHoursPerWeek',
                                    formatFloat(e.target.value),
                                    'minimumHoursPerWeek'
                                )
                            }
                        />
                    </div>
                    <div className={local.sectionColumn2}>
                        <div className={app.fieldHeading}>
                            Tell us about your preferred working hours
                        </div>
                        <div className={local.subheading}>
                            These will be used as a guide to your ideal working hours. We’ll try our
                            best, where possible, to match them.
                        </div>
                        <div className={local.hoursRow} data-testid="week_times">
                            <span className={local.label}> Monday - Friday </span>
                            <TimeSelect
                                id="weekdayStartTime"
                                label="Start Time"
                                disabled={isDisabled}
                                value={newEntry.weekdayStartTime || ''}
                                min={'00:00'}
                                max={'24:00'}
                                error={errors.weekdayStartTime}
                                onTimeChange={(e) =>
                                    onUpdateTimes(
                                        'weekdayStartTime',
                                        e.target.value,
                                        'weekdayEndTime'
                                    )
                                }
                            />
                            <TimeSelect
                                id="weekdayEndTime"
                                label="End Time"
                                disabled={isDisabled}
                                value={newEntry.weekdayEndTime || ''}
                                min={'00:00'}
                                max={'24:00'}
                                error={errors.weekdayEndTime}
                                onTimeChange={(e) =>
                                    onUpdateTimes(
                                        'weekdayEndTime',
                                        e.target.value,
                                        'weekdayStartTime'
                                    )
                                }
                            />
                        </div>
                        <div className={local.hoursRow} data-testid="saturday_times">
                            <span className={local.label}> Saturday </span>
                            <TimeSelect
                                id="saturdayStartTime"
                                label="Start Time"
                                disabled={isDisabled}
                                value={newEntry.saturdayStartTime || ''}
                                min={'00:00'}
                                max={'24:00'}
                                error={errors.saturdayStartTime}
                                onTimeChange={(e) =>
                                    onUpdateTimes(
                                        'saturdayStartTime',
                                        e.target.value,
                                        'saturdayEndTime'
                                    )
                                }
                            />
                            <TimeSelect
                                id="saturdayEndTime"
                                label="End Time"
                                disabled={isDisabled}
                                value={newEntry.saturdayEndTime || ''}
                                min={'00:00'}
                                max={'24:00'}
                                error={errors.saturdayEndTime}
                                onTimeChange={(e) =>
                                    onUpdateTimes(
                                        'saturdayEndTime',
                                        e.target.value,
                                        'saturdayStartTime'
                                    )
                                }
                            />
                        </div>
                        <div className={local.hoursRow} data-testid="sunday_times">
                            <span className={local.label}> Sunday </span>
                            <TimeSelect
                                id="sundayStartTime"
                                label="Start Time"
                                disabled={isDisabled}
                                value={newEntry.sundayStartTime || ''}
                                min={'00:00'}
                                max={'24:00'}
                                error={errors.sundayStartTime}
                                onTimeChange={(e) =>
                                    onUpdateTimes(
                                        'sundayStartTime',
                                        e.target.value,
                                        'sundayEndTime'
                                    )
                                }
                            />
                            <TimeSelect
                                id="sundayEndTime"
                                label="End Time"
                                disabled={isDisabled}
                                value={newEntry.sundayEndTime || ''}
                                min={'00:00'}
                                max={'24:00'}
                                error={errors.sundayEndTime}
                                onTimeChange={(e) =>
                                    onUpdateTimes(
                                        'sundayEndTime',
                                        e.target.value,
                                        'sundayStartTime'
                                    )
                                }
                            />
                        </div>
                    </div>
                </div>

                <TextAreaField
                    id="workingHoursNotes"
                    label="Working Hour Notes"
                    placeholder="Add additional working hour details here"
                    disabled={isDisabled}
                    rows={8}
                    maxLength={NOTES_CHAR_LIMIT}
                    value={newEntry.workingHoursNotes}
                    counter={'true'}
                    count={`${newEntry.workingHoursNotes?.length || 0}/${NOTES_CHAR_LIMIT}`}
                    onChange={(e) => onUpdate('workingHoursNotes', e.target.value)}
                />

                <h2 className={app.sectionHeading}>Travel Overview</h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="maximumTravelToWorkId"
                            key={keys.maximumTravelToWorkId}
                            label="Maximum Travel To Work"
                            placeholder="Select maximum travel to work..."
                            disabled={isDisabled}
                            menuItems={maximumTravelDistanceDetails || []}
                            selectedId={newEntry.maximumTravelToWorkId || ''}
                            selected={getObjFromId(
                                maximumTravelDistanceDetails,
                                newEntry.maximumTravelToWorkId
                            )}
                            sort={false}
                            onChange={(chosenId) => onUpdate('maximumTravelToWorkId', chosenId)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <MultiSelect
                            id="methodOfTransportIds"
                            key={keys.methodOfTransportIds}
                            label="Available Method(s) Of Transport"
                            placeholder="Select available methods of transport..."
                            disabled={isDisabled}
                            menuItems={transportMethodDetails || []}
                            preSelectedIds={newEntry.methodOfTransportIds || []}
                            preSelects={
                                transportMethodDetails.filter((el) =>
                                    newEntry.methodOfTransportIds.includes(el.id)
                                ) || []
                            }
                            onChange={(chosenIds) => onUpdate('methodOfTransportIds', chosenIds)}
                        />
                    </div>
                </div>
                <Button
                    id="workRequirementsUpdateButton"
                    disabled={isDisabled || isButtonDisabled}
                    content="Update Work Requirements"
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

export default WorkRequirements;
